<template>
  <div class="location-container">
    <!-- Header / controls -->
    <div class="header-section">
      <h3 class="page-title">Articles at Location</h3>

      <!-- Location: scan or type "18-V 9 A Box98" -->
      <div class="location-controls">
        <b-form-input
          v-model="locationInput"
          v-on:keydown.enter="searchLocation"
          v-on:keyup.enter="searchLocation"
          @change="onLocationChange"
          @focus="$event.target.select()"
          @click="$event.target.select()"
          ref="location"
          type="text"
          size="lg"
          inputmode="none"
          virtualkeyboardpolicy="manual"
          autocomplete="off"
          autocapitalize="characters"
          class="mobile-input location-input"
          placeholder="SCAN OR TYPE LOCATION"></b-form-input>
        <b-button size="lg" variant="success" class="location-btn" @click="searchLocation">
          <b-icon-search></b-icon-search>
        </b-button>
        <b-button size="lg" variant="outline-secondary" class="location-btn" @click="showManual = !showManual">
          {{ showManual ? 'HIDE' : 'SHOW' }}
        </b-button>
      </div>

      <!-- Manual selectors, collapsed by default -->
      <b-collapse v-model="showManual" class="manual-selectors">
        <b-form-select v-model="selectors.rafa" :options="locations.rafe"></b-form-select>
        <b-form-select
          v-model="selectors.kolona"
          :disabled="!selectors.rafa"
          :options="locations.kolone"></b-form-select>
        <b-form-select
          v-model="selectors.red"
          :disabled="!selectors.kolona"
          :options="locations.redovi"></b-form-select>
        <b-form-select
          v-model="selectors.box"
          :disabled="!selectors.red"
          :options="shelfBoxes"></b-form-select>
        <b-button
          variant="success"
          :disabled="!selectors.rafa || !selectors.kolona || !selectors.red"
          @click="getParts">
          <b-icon-search></b-icon-search> SEARCH
        </b-button>
      </b-collapse>

      <b-form-input
        v-model="scannedBox"
        v-on:keyup.enter="findScanned"
        ref="scanned"
        type="text"
        name="scanned"
        id="scanned"
        size="lg"
        inputmode="none"
        virtualkeyboardpolicy="manual"
        autocomplete="off"
        class="mobile-input scan-input"
        placeholder="SELECT INPUT AND SCAN"></b-form-input>
    </div>

    <div v-if="loading" class="loading-section">
      <b-icon icon="arrow-clockwise" animation="spin" font-scale="3"></b-icon>
    </div>

    <div v-else-if="products.length">
      <!-- Progress summary -->
      <div :class="['progress-summary', `status-${status.key}`]">
        <div class="summary-location">Location: {{ currentLocation }}</div>
        <div class="summary-grid">
          <div class="summary-card">
            <span class="summary-value">{{ totalCount }}</span>
            <span class="summary-label">Pieces</span>
          </div>
          <div class="summary-card">
            <span class="summary-value">{{ totalScanned }}</span>
            <span class="summary-label">Scanned</span>
          </div>
          <div class="summary-card">
            <span class="summary-value">{{ progressPercentage }}%</span>
            <span class="summary-label">Progress</span>
          </div>
        </div>
        <b-progress
          :value="totalScanned"
          :max="totalCount || 1"
          :variant="status.variant"
          height="8px"
          class="my-2"></b-progress>
        <div class="summary-status">{{ status.text }}</div>
      </div>

      <!-- Desktop table -->
      <div class="d-none d-md-block row mt-3 overflow-hidden">
        <table class="table" ref="articles">
          <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">Updated</th>
            <th scope="col">SKU</th>
            <th scope="col">Name</th>
            <th scope="col" class="text-center">Local Count</th>
            <th scope="col" class="text-center">Scanned</th>
            <th scope="col"></th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(val, i) of products" v-bind:key="i" :class="getRowClass(val)" :ref="`row-${i}`">
            <td>{{i+1}}</td>
            <td>{{!val.updatedAt ? "No updates" : moment(val.updatedAt).format('DD.MM.YYYY HH:mm')}}</td>
            <td>{{val.codename}}</td>
            <td @click="showImage(val)" class="pointer">{{val.name}}</td>
            <td class="text-center">{{val.localCount || 0}}</td>
            <td :class="[scannedIndex === i ? 'bg-info text-white' : '', 'text-center']">{{val.scanned || 0}}</td>
            <td class="text-center">
              <b-button
                v-if="qttEan(val)"
                :href="`/qtt/${qttEan(val)}`"
                target="_blank"
                size="sm"
                variant="outline-success"
                :title="`EAN ${qttEan(val)}`">
                <b-icon icon="box-arrow-up-right"></b-icon>
              </b-button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile cards -->
      <div class="d-md-none mobile-cards">
        <div
          v-for="(val, i) of products"
          :key="i"
          :class="['product-card', getCardClass(val), scannedIndex === i ? 'scanned-highlight' : '']"
          :ref="`row-${i}`">
          <div class="card-top">
            <span class="item-number">#{{ i + 1 }}</span>
            <span :class="['scan-count', getCardClass(val) || 'card-pending']">
              {{ val.scanned || 0 }} / {{ val.localCount || 0 }}
            </span>
          </div>
          <div class="card-main">
            <div class="product-name" @click="showImage(val)">{{ val.name }}</div>
            <div class="product-meta">
              <span>{{ val.codename }}</span>
              <span>{{ !val.updatedAt ? 'No updates' : moment(val.updatedAt).format('DD.MM.YY HH:mm') }}</span>
            </div>
          </div>
          <b-button
            v-if="qttEan(val)"
            :href="`/qtt/${qttEan(val)}`"
            target="_blank"
            variant="outline-success"
            class="qtt-btn"
            :title="`EAN ${qttEan(val)}`">
            <b-icon icon="box-arrow-up-right"></b-icon>
          </b-button>
        </div>
      </div>
    </div>

    <div v-else-if="hasSearched" class="progress-summary">
      <div class="summary-location">Location: {{ currentLocation }}</div>
      <div class="summary-status">No products at this location</div>
    </div>

    <b-modal ref="image-modal" id="image-modal" hide-footer title="Product image">
      <div class="d-block text-center">
        <b-img :src="articleImage" fluid alt="No image"></b-img>
      </div>
      <b-button class="mt-3" variant="outline-danger" block @click="$bvModal.hide('image-modal')">Close</b-button>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import fixed from '@/fixed';
import { parseLocation } from '@/utils';

const BASE_API_URL = fixed.API_URL;

export default {
  name: 'ProductsOnLocation.vue',
  data: () => ({
    loading: false,
    errorAudio: null,
    successAudio: null,
    locationInput: '',
    showManual: false,
    hasSearched: false,
    lastSearchAt: 0,
    selectors: {
      rafa: '',
      kolona: '',
      red: '',
      box: '',
    },
    locations: fixed.locations,
    products: [],
    articleImage: null,
    scannedBox: '',
    scannedIndex: null,
    shelfBoxes: [{ value: '', text: 'Select Box', disabled: true }],

    moment,
  }),
  computed: {
    currentLocation() {
      const {
        rafa, kolona, red, box,
      } = this.selectors;
      return [rafa, kolona, red, box].filter(Boolean).join(' ');
    },
    totalCount() {
      return this.products.reduce((sum, p) => sum + (p.localCount || 0), 0);
    },
    totalScanned() {
      return this.products.reduce((sum, p) => sum + (p.scanned || 0), 0);
    },
    progressPercentage() {
      if (!this.totalCount) return 0;
      return Math.round((this.totalScanned / this.totalCount) * 100);
    },
    status() {
      const diff = this.totalScanned - this.totalCount;
      const of = `${this.totalScanned} of ${this.totalCount} scanned`;
      if (!this.products.length) return { key: 'empty', variant: 'primary', text: '' };
      if (!diff) return { key: 'complete', variant: 'success', text: `✓ COMPLETE - ${of}` };
      if (diff < 0) return { key: 'missing', variant: 'primary', text: `! MISSING ${-diff} - ${of}` };
      return { key: 'extra', variant: 'warning', text: `! EXTRA ${diff} - ${of}` };
    },
  },
  mounted() {
    this.generateBoxes();
    this.initAudio();
    this.$refs.location.focus();
  },
  beforeDestroy() {
    this.errorAudio = null;
    this.successAudio = null;
  },
  methods: {
    initAudio() {
      if (!this.errorAudio) {
        this.errorAudio = new Audio('/error.mp3');
        this.errorAudio.preload = 'auto';
      }
      if (!this.successAudio) {
        this.successAudio = new Audio('/ok.mp3');
        this.successAudio.preload = 'auto';
      }
    },
    playErrorSound() {
      if (this.errorAudio) {
        this.errorAudio.currentTime = 0;
        this.errorAudio.play();
      }
    },
    playSuccessSound() {
      if (this.successAudio) {
        this.successAudio.currentTime = 0;
        this.successAudio.play();
      }
    },
    onLocationChange() {
      // Scanners that paste the location without a trailing Enter still search.
      if (!parseLocation(this.locationInput).error) this.searchLocation();
    },
    async searchLocation() {
      // Scanner wedges fire keydown and keyup for the same Enter press.
      if (Date.now() - this.lastSearchAt < 500) return;
      this.lastSearchAt = Date.now();

      const parsed = parseLocation(this.locationInput);
      if (parsed.error) {
        this.$toast.error(parsed.error);
        this.playErrorSound();
        this.$refs.location.$el.select();
        return;
      }

      const next = [parsed.rafa, parsed.kolona, parsed.red, parsed.box].filter(Boolean).join(' ');
      // Leaving a location that does not add up is almost always a mistake.
      if (this.products.length && next !== this.currentLocation && this.totalScanned !== this.totalCount) {
        this.playErrorSound();
        const leave = await this.$bvModal.msgBoxConfirm(
          `${this.status.text}. You are leaving ${this.currentLocation} with an inventory error.`,
          {
            title: 'Not all parts were scanned',
            okTitle: 'Continue anyway',
            okVariant: 'danger',
            cancelTitle: 'Stay here',
            centered: true,
          },
        );
        if (!leave) {
          this.$refs.scanned.focus();
          return;
        }
      }

      this.selectors = parsed;
      this.locationInput = next;
      await this.getParts();
    },
    async getParts() {
      if (!this.selectors.rafa || !this.selectors.kolona || !this.selectors.red) {
        this.$toast.warning('All selectors are required to build location');
        return;
      }

      this.loading = true;
      try {
        const result = await axios.get(`${BASE_API_URL}/admin/products-on-location`, {
          withCredentials: true,
          params: { l: `${this.selectors.rafa} ${this.selectors.kolona} ${this.selectors.red} ${this.selectors.box}` },
        });
        // Seed `scanned` up front so the counters stay reactive as it changes.
        this.products = (result.data || []).map((p) => ({ ...p, scanned: p.scanned || 0 }));
        this.scannedIndex = null;
        this.hasSearched = true;
        this.locationInput = this.currentLocation;
        this.findScanned();
        this.$nextTick(() => {
          if (this.products.length) this.$refs.scanned.focus();
          else this.$refs.location.$el.select();
        });
      } catch (e) {
        this.$toast.error('can\'t get articles for this location');
        console.error(e); // eslint-disable-line
      }
      this.loading = false;
    },
    showImage(article) {
      this.articleImage = article?.images?.image[0]?.url || null;
      if (!this.articleImage) return;

      this.$refs['image-modal'].show();
    },
    eansOf(product) {
      return (product?.productCodes?.productCode || [])
        .filter((c) => c.type === 'ean' && c.code)
        .map((c) => String(c.code));
    },
    // A product can carry several EAN entries; the quantity page wants the
    // real 13-digit barcode rather than whichever one happens to come first.
    qttEan(product) {
      const codes = this.eansOf(product).map((c) => c.replace(/\D/g, '')).filter(Boolean);
      return codes.find((c) => /^\d{13}$/.test(c)) || codes.find((c) => /^\d{8,14}$/.test(c)) || '';
    },
    findScanned() {
      if (!this.scannedBox) return;

      const index = this.products.findIndex((p) => this.eansOf(p).includes(this.scannedBox));
      this.scannedBox = '';

      if (index === -1) {
        this.$toast.error('product with that code is not on the list');
        this.playErrorSound();
        return;
      }

      this.products[index].scanned += 1;
      this.scannedIndex = index;

      // Shelf finished: hand the operator straight back to the location field.
      if (this.totalCount && this.totalScanned === this.totalCount) {
        this.playSuccessSound();
        this.$toast.success('Complete - scan the next shelf');
        this.$nextTick(() => this.$refs.location.focus());
        return;
      }

      this.$nextTick(() => {
        const row = this.$refs[`row-${index}`];
        if (row) {
          (Array.isArray(row) ? row[0] : row).scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },
    getRowClass(p) {
      if ((p.localCount || 0) === p.scanned) return ['bg-success'];
      if ((p.localCount || 0) < p.scanned) return ['bg-warning', 'text-dark'];
      return '';
    },
    getCardClass(p) {
      if ((p.localCount || 0) === p.scanned) return 'card-success';
      if ((p.localCount || 0) < p.scanned) return 'card-warning';
      return '';
    },
    generateBoxes() {
      for (let i = 1; i <= 250; i += 1) {
        this.shelfBoxes.push({ value: `Box${i}`, text: `Box ${i}` });
      }
    },
  },
};
</script>

<style scoped>
.location-container {
  padding: 0.5rem 0 2rem;
}

.header-section {
  background-color: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  padding: 1rem;
  margin-bottom: 1rem;
}

.page-title {
  font-size: 1.6rem;
  font-weight: 750;
  letter-spacing: -0.025em;
  text-align: left !important;
  margin: 0 0 1rem !important;
}

.location-controls {
  display: flex;
  gap: 0.5rem;
}

/* `form-control-lg` and `btn-lg` size themselves from different font-size and
   padding values, so they never match by accident - pin one height for both. */
.location-controls > .form-control,
.location-controls > .btn {
  height: 48px;
  padding-top: 0;
  padding-bottom: 0;
}

.location-controls > .btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.location-input {
  text-transform: uppercase;
}

.location-btn {
  flex: 0 0 auto;
  min-width: 56px;
}

.manual-selectors {
  display: grid;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.mobile-input {
  border: 2px solid var(--border-strong);
  border-radius: var(--radius-sm);
}

.scan-input {
  margin-top: 0.75rem;
}

.loading-section {
  display: flex;
  justify-content: center;
  padding: 3rem;
}

/* Progress summary - kept in view while scanning a shelf */
.progress-summary {
  position: sticky;
  /* Published by Navbar.vue; 0 when the navbar isn't rendered. */
  top: var(--navbar-height, 0px);
  z-index: 20;
  background-color: var(--surface);
  border: 2px solid var(--border-strong);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
  padding: 0.75rem;
}

.summary-location {
  font-size: 0.85rem;
  color: var(--text-muted);
  margin-bottom: 0.4rem;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  text-align: center;
}

.summary-value {
  display: block;
  font-size: 1.5rem;
  font-weight: bold;
  line-height: 1.1;
}

.summary-label {
  display: block;
  font-size: 0.72rem;
  text-transform: uppercase;
  color: var(--text-muted);
}

.summary-status {
  font-weight: bold;
  text-align: center;
}

.status-complete {
  border-color: var(--success);
}

.status-complete .summary-status {
  color: var(--success);
}

.status-extra {
  border-color: var(--warning);
}

.status-extra .summary-status {
  color: var(--warning);
}

.pointer, .product-name {
  cursor: pointer;
}

/* Mobile cards */
.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.product-card {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.5rem;
  background-color: var(--surface);
  border: 1px solid var(--border);
  border-left: 5px solid var(--info);
  border-radius: var(--radius-sm);
  padding: 0.6rem;
}

.card-success {
  border-left-color: var(--success);
}

.card-warning {
  border-left-color: var(--warning);
}

.scanned-highlight {
  box-shadow: 0 0 0 2px var(--accent);
}

.card-top {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
}

.item-number {
  font-size: 0.75rem;
  color: var(--text-faint);
}

.scan-count {
  font-weight: bold;
  white-space: nowrap;
}

.scan-count.card-success {
  color: var(--success);
}

.scan-count.card-warning {
  color: var(--warning);
}

.scan-count.card-pending {
  color: var(--info);
}

.product-name {
  font-weight: 600;
  overflow-wrap: anywhere;
}

.product-meta {
  display: flex;
  gap: 0.75rem;
  font-size: 0.75rem;
  color: var(--text-muted);
}

.qtt-btn {
  min-width: 44px;
  min-height: 44px;
}

@media (max-width: 767.98px) {
  .header-section {
    padding: 0.6rem;
    margin-bottom: 0.5rem;
  }

  .page-title {
    font-size: 1.05rem;
    margin-bottom: 0.5rem;
  }

  .location-controls > .form-control,
  .location-controls > .btn {
    font-size: 1rem;
  }

  .scan-input {
    min-height: 48px;
    font-size: 1rem;
  }
}

@media (min-width: 768px) {
  .manual-selectors {
    grid-template-columns: repeat(4, 1fr) auto;
    align-items: center;
  }
}
</style>
